import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Tipping is voluntary. Nothing on Quibblies is ever paywalled behind a tip.
 * Amounts are always integers in the smallest currency unit and the platform
 * fee is applied in the database, so the client can never dictate the split.
 */
export const createTip = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      recipientWriterId: string;
      publicationId?: string | null;
      amountMinor: number;
      currency: string;
      message?: string;
      anonymous?: boolean;
      /** Present when the browser can complete a card payment. */
      environment?: "sandbox" | "live";
      returnUrl?: string;
      /** One key per tip attempt; a repeat returns the first tip untouched. */
      idempotencyKey?: string;
    }) => {
      if (!input?.recipientWriterId) throw new Error("Missing writer");
      if (!Number.isInteger(input.amountMinor) || input.amountMinor <= 0) {
        throw new Error("Tip amount must be a whole number of pence or cents");
      }
      if (!/^[A-Z]{3}$/.test(input.currency ?? "")) throw new Error("Invalid currency");
      const environment =
        input.environment === "sandbox" || input.environment === "live"
          ? input.environment
          : null;
      const returnUrl =
        typeof input.returnUrl === "string" && /^https?:\/\//.test(input.returnUrl)
          ? input.returnUrl
          : null;
      const idempotencyKey =
        typeof input.idempotencyKey === "string" && /^[A-Za-z0-9_-]{8,64}$/.test(input.idempotencyKey)
          ? input.idempotencyKey
          : null;
      return {
        recipientWriterId: input.recipientWriterId,
        publicationId: input.publicationId ?? null,
        amountMinor: input.amountMinor,
        currency: input.currency,
        message: typeof input.message === "string" ? input.message.trim().slice(0, 500) : null,
        anonymous: Boolean(input.anonymous),
        environment,
        returnUrl,
        idempotencyKey,
      };
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Minimum, self-tipping, currency support and the writer's tipping opt-in
    // are all enforced by the database, not just here.
    const { data: currency, error: currencyError } = await supabase
      .from("currencies")
      .select("code, minimum_tip_minor, symbol")
      .eq("code", data.currency)
      .eq("enabled", true)
      .maybeSingle();
    if (currencyError) throw new Error(currencyError.message);
    if (!currency) throw new Error("That currency is not supported yet");
    if (data.amountMinor < currency.minimum_tip_minor) {
      throw new Error("That tip is below the minimum amount");
    }

    // A retried request (double click, flaky connection, a client retry) must
    // never create a second tip or a second charge: the key is unique per
    // sender in the database, and the first tip is handed straight back.
    if (data.idempotencyKey) {
      const { data: existing } = await supabase
        .from("tips")
        .select(
          "id, amount_minor, platform_fee_minor, net_amount_minor, currency, status, payment_reference",
        )
        .eq("sender_user_id", userId)
        .eq("idempotency_key", data.idempotencyKey)
        .maybeSingle();

      if (existing) {
        let clientSecret: string | null = null;
        if (data.environment && existing.payment_reference?.startsWith("cs_")) {
          const { getCheckoutClientSecret } = await import(
            "@/lib/payments/stripe-tips.server"
          );
          clientSecret = await getCheckoutClientSecret(
            existing.payment_reference,
            data.environment,
          );
        }
        return {
          id: existing.id,
          amountMinor: existing.amount_minor,
          feeMinor: existing.platform_fee_minor,
          netMinor: existing.net_amount_minor,
          currency: existing.currency,
          status: existing.status,
          clientSecret,
          checkoutUrl: null as string | null,
          liveMode: data.environment === "live",
          message: "This tip was already started — resuming it instead of creating a new one.",
        };
      }
    }

    const { data: tip, error } = await supabase
      .from("tips")
      .insert({
        sender_user_id: userId,
        recipient_writer_id: data.recipientWriterId,
        publication_id: data.publicationId,
        amount_minor: data.amountMinor,
        currency: data.currency,
        message: data.message,
        anonymous: data.anonymous,
        status: "pending",
        idempotency_key: data.idempotencyKey,
      })
      .select("id, amount_minor, platform_fee_minor, net_amount_minor, currency, status")
      .single();
    if (error) {
      // Unique violation: two requests raced; the winner's tip is the tip.
      if (error.code === "23505" && data.idempotencyKey) {
        throw new Error("That tip is already being processed.");
      }
      throw new Error(error.message);
    }


    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Card payments go through Stripe's hosted, embedded checkout. Quibblies
    // never sees or stores card data — only the session reference, so the
    // signed webhook can settle the tip when Stripe confirms the outcome.
    if (data.environment && data.returnUrl) {
      const { data: writer } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("id", data.recipientWriterId)
        .maybeSingle();
      const { data: authUser } = await supabase.auth.getUser();

      const { createTipCheckoutSession } = await import("@/lib/payments/stripe-tips.server");
      // If the writer has finished payout onboarding, this tip is split at
      // the moment of payment and never lands on the manual ledger.
      const { getConnectAccount } = await import("@/lib/payments/connect.server");
      const connect = await getConnectAccount(data.recipientWriterId, data.environment);
      const autoSettles = Boolean(connect?.chargesEnabled && connect?.payoutsEnabled);
      // Stripe returns the reader to the page they tipped from, carrying the
      // tip id so the receipt timeline can be shown straight away.
      const returnUrl = new URL(data.returnUrl);
      returnUrl.searchParams.set("tip", tip.id);
      const checkout = await createTipCheckoutSession({
        tipId: tip.id,
        amountMinor: tip.amount_minor,
        currency: tip.currency,
        writerName: writer?.display_name ?? "a Quibblies writer",
        recipientWriterId: data.recipientWriterId,
        senderUserId: userId,
        senderEmail: authUser?.user?.email ?? undefined,
        returnUrl: returnUrl.toString(),
        environment: data.environment,
        destinationAccountId: autoSettles ? connect!.stripeAccountId : null,
        applicationFeeMinor: autoSettles ? tip.platform_fee_minor : null,
      });

      if (checkout.error || !checkout.clientSecret) {
        await supabaseAdmin
          .from("tips")
          .update({ status: "failed", payment_provider: "stripe" })
          .eq("id", tip.id);
        throw new Error(checkout.error ?? "Could not start the payment");
      }

      await supabaseAdmin
        .from("tips")
        .update({
          payment_provider: "stripe",
          payment_reference: checkout.reference,
          status: "pending",
          settlement_mode: autoSettles ? "connect" : "manual",
          destination_account_id: autoSettles ? connect!.stripeAccountId : null,
        })
        .eq("id", tip.id);

      return {
        id: tip.id,
        amountMinor: tip.amount_minor,
        feeMinor: tip.platform_fee_minor,
        netMinor: tip.net_amount_minor,
        currency: tip.currency,
        status: "pending" as const,
        clientSecret: checkout.clientSecret,
        checkoutUrl: null as string | null,
        liveMode: data.environment === "live",
        message: "Complete your card details to send this tip.",
      };
    }

    // Fallback: record the intent only (used where checkout is unavailable).
    const { getPaymentProvider } = await import("@/lib/payments/provider.server");
    const provider = getPaymentProvider();
    const intent = await provider.createOneTimePayment({
      amountMinor: tip.amount_minor,
      currency: tip.currency,
      description: `Quibblies tip ${tip.id}`,
      metadata: { tip_id: tip.id, recipient: data.recipientWriterId },
    });

    await supabaseAdmin
      .from("tips")
      .update({
        payment_provider: intent.provider,
        payment_reference: intent.reference,
        status: intent.status === "completed" ? "completed" : "pending",
        completed_at: intent.status === "completed" ? new Date().toISOString() : null,
      })
      .eq("id", tip.id);

    return {
      id: tip.id,
      amountMinor: tip.amount_minor,
      feeMinor: tip.platform_fee_minor,
      netMinor: tip.net_amount_minor,
      currency: tip.currency,
      status: intent.status,
      clientSecret: null as string | null,
      checkoutUrl: intent.checkoutUrl,
      liveMode: provider.liveMode,
      message: intent.message,
    };
  });

/** Tips received by the signed-in writer. Anonymity is enforced in the database. */
export const getMyTips = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.rpc("writer_tips", { _limit: 200 });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/**
 * Platform-wide financial view. Restricted to the Owner and Administrators who
 * have been explicitly granted financial access — Moderators never qualify.
 */
export const getFinancialOverview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: allowed, error: accessError } = await supabase.rpc("has_financial_access", {
      _user_id: userId,
    });
    if (accessError) throw new Error(accessError.message);
    if (!allowed) throw new Error("Forbidden: financial access is required");

    const { data, error } = await supabase
      .from("tips")
      .select(
        "id, amount_minor, platform_fee_minor, net_amount_minor, currency, status, anonymous, payment_provider, payment_reference, created_at, completed_at, refunded_at, recipient_writer_id, sender_user_id, publication_id",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);

    const rows = data ?? [];
    const ids = Array.from(
      new Set(rows.flatMap((r) => [r.recipient_writer_id, r.sender_user_id].filter(Boolean))),
    ) as string[];

    const { data: profiles } = ids.length
      ? await supabase.from("profiles").select("id, username, display_name").in("id", ids)
      : { data: [] as { id: string; username: string; display_name: string }[] };

    const nameOf = (id: string | null) =>
      (profiles ?? []).find((p) => p.id === id)?.display_name ?? null;

    const totals = rows.reduce(
      (acc, row) => {
        const bucket = (acc[row.currency] ??= {
          currency: row.currency,
          completed: 0,
          fees: 0,
          refunded: 0,
          failed: 0,
          disputed: 0,
        });
        if (row.status === "completed") {
          bucket.completed += row.amount_minor;
          bucket.fees += row.platform_fee_minor;
        }
        if (row.status === "refunded") bucket.refunded += row.amount_minor;
        if (row.status === "failed") bucket.failed += row.amount_minor;
        if (row.status === "disputed") bucket.disputed += row.amount_minor;
        return acc;
      },
      {} as Record<
        string,
        {
          currency: string;
          completed: number;
          fees: number;
          refunded: number;
          failed: number;
          disputed: number;
        }
      >,
    );

    return {
      totals: Object.values(totals),
      transactions: rows.map((row) => ({
        id: row.id,
        amountMinor: row.amount_minor,
        feeMinor: row.platform_fee_minor,
        netMinor: row.net_amount_minor,
        currency: row.currency,
        status: row.status,
        provider: row.payment_provider,
        reference: row.payment_reference,
        createdAt: row.created_at,
        writer: nameOf(row.recipient_writer_id),
        // Administrators retain internal sender identity for financial compliance,
        // even where the tip is anonymous to the writer.
        sender: nameOf(row.sender_user_id),
        anonymousToWriter: row.anonymous,
      })),
    };
  });

/**
 * The platform fee, published openly so the tipping dialog and Creator Studio
 * always show the same number the database applies. Non-sensitive.
 */
export const getTipFeeInfo = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { getPaymentProvider } = await import("@/lib/payments/provider.server");

  const { data } = await supabaseAdmin
    .from("platform_settings")
    .select("value")
    .eq("key", "tip_fee_basis_points")
    .maybeSingle();

  const parsed = Number.parseInt(String(data?.value ?? 500), 10);
  const provider = getPaymentProvider();
  return {
    feeBasisPoints: Number.isFinite(parsed) ? parsed : 500,
    liveMode: provider.liveMode,
    provider: provider.id,
  };
});

/**
 * Receipt for a tip the signed-in reader sent: the payment reference, each
 * timestamp Stripe has confirmed, and the final outcome. Scoped to the sender,
 * so a tip id alone never reveals somebody else's payment.
 */
export const getTipStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { tipId: string }) => {
    if (!input?.tipId) throw new Error("Missing tip");
    return { tipId: input.tipId };
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: tip, error } = await supabase
      .from("tips")
      .select(
        "id, amount_minor, platform_fee_minor, net_amount_minor, currency, status, payment_provider, payment_reference, created_at, completed_at, refunded_at, recipient_writer_id, publication_id",
      )
      .eq("id", data.tipId)
      .eq("sender_user_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!tip) throw new Error("That tip could not be found");

    const { data: writer } = await supabase
      .from("profiles")
      .select("display_name, username")
      .eq("id", tip.recipient_writer_id)
      .maybeSingle();

    let publicationTitle: string | null = null;
    if (tip.publication_id) {
      const { data: story } = await supabase
        .from("stories")
        .select("title")
        .eq("id", tip.publication_id)
        .maybeSingle();
      publicationTitle = story?.title ?? null;
    }

    // The supporter owns this tip, so they may see why its status changed —
    // the provider events are read with elevated rights but filtered to
    // this one tip and stripped down to an explanation.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: events } = await supabaseAdmin
      .from("payment_webhook_events")
      .select("event_type, result, received_at, payload")
      .eq("tip_id", tip.id)
      .order("received_at", { ascending: true })
      .limit(20);

    const history = (events ?? []).map((event) => {
      const payload = (event.payload ?? {}) as Record<string, unknown>;
      const reason =
        typeof payload['reason'] === "string"
          ? (payload['reason'] as string)
          : typeof payload['failure_reason'] === "string"
            ? (payload['failure_reason'] as string)
            : null;
      return {
        event: event.event_type,
        outcome: event.result,
        at: event.received_at,
        reason,
        source: typeof payload['source'] === "string" ? (payload['source'] as string) : "provider",
      };
    });

    const latestReason =
      [...history].reverse().find((entry) => entry.reason)?.reason ?? null;

    const settled = tip.status !== "pending";
    return {
      id: tip.id,
      amountMinor: tip.amount_minor,
      feeMinor: tip.platform_fee_minor,
      netMinor: tip.net_amount_minor,
      currency: tip.currency,
      status: tip.status,
      provider: tip.payment_provider,
      reference: tip.payment_reference,
      createdAt: tip.created_at,
      completedAt: tip.completed_at,
      refundedAt: tip.refunded_at,
      settled,
      writerName: writer?.display_name ?? null,
      writerUsername: writer?.username ?? null,
      publicationId: tip.publication_id ?? null,
      publicationTitle,
      history,
      reason: latestReason,
    };
  });
